import {
  cloneDeep,
  every,
  get,
  isArray,
  isEmpty,
  isMatch,
  isMatchWith,
  isNaN,
  isPlainObject,
  isString,
  some,
  toPlainObject,
} from 'lodash';
import { AnyRecord, Entry, Path } from 'types';

export const copyToClipboard = (text: string): void => {
  const textField = document.createElement('textarea');
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
};

export const splitCommaSeparatedArray = (str: string): Array<string> => {
  const value = str.split?.(',');
  return typeof value === 'string' ? [value] : value;
};

export const formatNumberToCurrency = (
  number?: number,
  options?: {
    currency?: string;
    locale?: string;
    hideSymbol?: boolean;
    hideCommas?: boolean;
  }
): string | null => {
  const { currency = 'USD', locale = 'en-Us', hideSymbol, hideCommas } = options || {};

  if (number === undefined || number === null) return null;

  const out = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency,
    style: hideSymbol ? undefined : 'currency',
  }).format(number);

  if (hideCommas) {
    return replaceAll(out, ',', '');
  }

  return out;
};

export const formatBytes = (bytes: number, decimals?: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals || 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const getInitials = (username?: string): string | undefined => {
  if (username === undefined) return undefined;

  const nameSplit = username?.toUpperCase().split(' ');

  return `${nameSplit[0][0]}${nameSplit[1]?.[0] || ''}`;
};

export const fromEntries = (entries: Array<Entry>): Record<string, any> =>
  entries.reduce((acc, [key, v]) => ({ ...acc, [key]: v }), {});

export const deepObjectCopy = <T = AnyRecord>(obj: T): T => cloneDeep(obj);

const matchEntry = (entry: Entry, entriesToMatch: Array<Entry>) =>
  some(entriesToMatch, (entryToMatch) => JSON.stringify(entryToMatch) === JSON.stringify(entry));

type JsonInput = Record<string, unknown> | Array<unknown>;

export const getJsonEntries = ({
  json,
  onSubmit,
  parentPath = [],
  entriesToMatch,
}: {
  json: JsonInput;
  onSubmit: (path: Array<string>) => void;
  parentPath?: Array<string>;
  entriesToMatch: Array<Entry>;
}): void => {
  if (json instanceof Array) {
    json.forEach((el, index) => {
      getJsonEntries({
        json: el as JsonInput,
        onSubmit,
        parentPath: [...parentPath, index.toString()],
        entriesToMatch,
      });
    });
    return;
  }

  if (json instanceof Object) {
    Object.keys(json).forEach((key) => {
      const value = json[key];
      if (matchEntry([key, value], entriesToMatch)) {
        onSubmit?.(parentPath);
      }
      if (value instanceof Object || value instanceof Array) {
        getJsonEntries({
          json: value as JsonInput,
          onSubmit,
          parentPath: [...parentPath, key],
          entriesToMatch,
        });
      }
    });
  }
};

export const getEntriesPaths = ({
  json,
  entriesToMatch,
}: {
  json: JsonInput;
  entriesToMatch: Array<Entry>;
}): Array<Array<string>> => {
  const paths: Array<Array<string>> = [];

  getJsonEntries({
    json,
    onSubmit: (path: Array<string>) => paths.push(path),
    entriesToMatch,
  });

  return paths;
};

export const isValid = (value: unknown): boolean => {
  switch (typeof value) {
    case 'undefined':
      return false;
    case 'string':
      return replaceAll(value, '\n', '') !== '';
    case 'object':
      return !isEmpty(isPlainObject(value) ? value : toPlainObject(value));
    default:
      return true;
  }
};

export const toCapitalLetter = (str = ''): string => `${str[0]?.toUpperCase()}${str?.slice?.(1)}`;

export const range = (count = 0): Array<number> => [...Array(count).keys()];

export const line = <T = undefined>(count = 0, fill?: T): Array<T> =>
  range(count).map(() => fill as T);

export const someMatriz = (data?: Array<Array<any>>, matcher?: (d: any) => boolean): boolean =>
  some(data || [[]], (rowData) => some(rowData, matcher || Boolean));

export const everyMatriz = (data?: Array<Array<any>>, matcher?: (d: any) => boolean): boolean =>
  every(data || [[]], (rowData) => every(rowData, matcher || Boolean));

export const mapArray = (
  objs: Array<Record<string, unknown> | string>,
  path?: Array<string>
): Array<string | null | undefined> => {
  const validToMap = path && path?.length && objs?.[0] instanceof Object;

  return validToMap ? objs?.map((obj) => get(obj, path)) : objs;
};

export const replaceAll = (value: string, match: string, replace: string): string =>
  value.split(match).join(replace);

export const removeInvalidFromJson = (value: Record<string, any>): Record<string, any> =>
  Object.entries(value).reduce((acc, [k, v]) => (isValid(v) ? { ...acc, [k]: v } : acc), {});

export const isPartialObject = (partial: AnyRecord, full: AnyRecord): boolean =>
  isMatch(full, partial);

export const pushStringToUniqueArray = (value: string, array: Array<string>): void => {
  if (!array.includes(value)) array.push(value);
};

export const existPath = (paths: Array<Path> = [], path: Path): boolean => {
  const pathStr = JSON.stringify(path);
  return paths.some((p) => JSON.stringify(p) === pathStr);
};

export const pushPathToUniqueArray = (paths: Array<Path>, path: Path): Array<Path> => {
  if (!existPath(paths, path)) {
    paths.push(path);
  }
  return paths;
};

export const addRow = <T extends any = any>(data: Array<T>, rowData: T): Array<T> => {
  const newData = deepObjectCopy(data);
  newData.push(rowData);
  return newData;
};

export const removeRow = <T extends any = any>(data: Array<T>, index: number): Array<T> => {
  const newData = deepObjectCopy(data);
  newData.splice(index, 1);
  return newData;
};

export const updateRow = <T extends any = any>(
  data: Array<T>,
  rowData: T,
  index: number
): Array<T> => {
  const newData = deepObjectCopy(data);
  newData[index] = rowData;
  return newData;
};

export const relocateRow = <T extends any = any>(
  data: Array<T>,
  fromIndex: number,
  toIndex: number
): Array<T> => {
  const newData = deepObjectCopy(data);

  const element = newData.splice(fromIndex, 1)[0];
  newData.splice(toIndex, 0, element);

  return newData;
};

export const filterJsonRow = <T extends AnyRecord = AnyRecord>(
  data: Array<T>,
  partialRow: Partial<T>
): Array<T> => {
  return data.filter((row) =>
    isMatchWith(row, partialRow, (objValue, srcValue) => {
      if (isString(objValue) && isString(srcValue)) {
        return objValue.toLocaleLowerCase().includes(srcValue.toLocaleLowerCase());
      }
      if (
        isArray(objValue) &&
        isString(objValue[0]) &&
        isArray(srcValue) &&
        isString(srcValue[0])
      ) {
        return objValue.some((objVal) =>
          srcValue.some((srcVal) => objVal.toLocaleLowerCase().includes(srcVal.toLocaleLowerCase()))
        );
      }
      return undefined;
    })
  );
};

export const isNullOrUndefined = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

export const isNullOrUndefinedOrEmptyString = (value: unknown): value is null | undefined | '' =>
  isNullOrUndefined(value) || value === '';

export const isStringOrNumber = (value: unknown): value is string | number =>
  typeof value === 'string' || typeof value === 'number';

export const shouldHasMessage = (text: string): void => {
  console.log(`Should has ${text}`);
};

export const getNumbersFromString = (string: string): string => {
  return string.replace?.(/[^0-9.]+/g, '');
};

/**
 * Converts a JSON path as Array<string> to JSON path as string
 */
export const fromPath = (path: Path): string => {
  return path.reduce((acc, e) => {
    const isNumber = !isNaN(Number(e));

    if (!acc) return e;

    return `${acc}${isNumber ? `[${e}]` : `.${e}`}`;
  }, '');
};

export const getJSONValues = (data: AnyRecord, paths: Array<Path>): Array<any> =>
  paths.map((path) => get(data, path));
